import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts/core"
import { PieChart } from "echarts/charts"
import { TooltipComponent, LegendComponent } from "echarts/components"
import { CanvasRenderer } from "echarts/renderers"

echarts.use([PieChart, TooltipComponent, LegendComponent, CanvasRenderer])

// Migration progress across the whole book, as one shape.
//
// A donut rather than a pie so the headline — how many eligible accounts are ready — sits in the
// middle, where a reader lands first. The slices answer the follow-up: of the rest, how many are
// waiting on a connection versus part-way through setup.
//
// Scoped to ELIGIBLE accounts. An account Data Manager would not replace anything for is not
// progress outstanding, and including it would hold the ratio down forever.
//
// Every slice arrives computed. This controller decides how they are drawn and nothing about what
// they mean.
export default class extends Controller {
  static targets = ["canvas", "empty"]
  static values = {
    // [{ name: String, value: Number, color: String }], largest-meaning-first.
    slices: Array,
    // The donut's centre: a percentage and its caption.
    centerValue: String,
    centerLabel: String,
  }

  connect() {
    // Nothing eligible on the book yet — an empty donut reads as a bug rather than as a state,
    // so the markup's fallback stays and the chart never initialises.
    if (this.slicesValue.every((slice) => slice.value === 0)) {
      this.emptyTarget.hidden = false
      return
    }

    this.chart = echarts.init(this.canvasTarget)
    this.chart.setOption(this.option())

    this.resizeObserver = new ResizeObserver(() => this.chart.resize())
    this.resizeObserver.observe(this.canvasTarget)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.chart?.dispose()
  }

  option() {
    return {
      tooltip: {
        trigger: "item",
        formatter: "{b}: {c} ({d}%)",
      },
      // No chart legend. The page renders its own beside this, where each entry carries the
      // count and filters the table on click — two legends would say the same thing, and only
      // one of them would do anything.
      graphic: {
        type: "group",
        // "center", not a percentage. echarts positions a graphic group by pinning its bounding
        // rect's LEFT EDGE at the resolved offset, while a pie's `center` is its midpoint — so
        // writing the same percentage for both anchors two different things and the text runs
        // off to the right of the hole. "center" is the one value that centres the rect, and it
        // centres on 50%, which is why the pie sits at 50% too rather than inset for a legend
        // that this chart does not draw.
        left: "center",
        top: "middle",
        children: [
          {
            type: "text",
            top: -18,
            style: {
              text: this.centerValueValue,
              textAlign: "center",
              fill: "#343B39", // sg-gray-800
              font: "600 26px sans-serif",
            },
          },
          {
            type: "text",
            top: 12,
            style: {
              text: this.centerLabelValue,
              textAlign: "center",
              fill: "#55615F", // sg-gray-600
              font: "12px sans-serif",
            },
          },
        ],
      },
      series: [
        {
          type: "pie",
          // A donut, so the centre can carry the headline.
          radius: ["58%", "82%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          // Slice labels would collide at this size; the legend and tooltip carry the names.
          label: { show: false },
          labelLine: { show: false },
          data: this.slicesValue.map((slice) => ({
            name: slice.name,
            value: slice.value,
            itemStyle: { color: slice.color },
          })),
        },
      ],
    }
  }
}
